// OWNER: Agent C. Net worth legible in under a second: hero number first,
// then investments vs loans, then the IN/UK split, then the trend line.
import { useMemo } from "react";
import * as echarts from "echarts";
import { Chart, ChartLegend, ChartPalette, TimeseriesChart } from "@cloudflare/kumo/components/chart";
import { Text } from "@cloudflare/kumo/components/text";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Button } from "@cloudflare/kumo/components/button";
import { ChartLineUpIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router";
import { formatInr, formatInrCompact, formatRelativeAge } from "@shared/format";
import { usePortfolioSummary, usePortfolioHistory } from "../../lib/hooks";
import { StalePriceBanner } from "../../components/StalePriceBanner";
import { GainBadge } from "../../components/GainBadge";
import { ChartSkeleton, KpiSkeleton } from "../../components/Skeletons";
import type { AccountKind, PortfolioSummary } from "@shared/types";

const COUNTRY_LABEL: Record<string, string> = { IN: "India", UK: "United Kingdom" };

// The country-split bar always uses categorical colors starting at index 0
// (one per country). Starting the "by investment type" pie past that range
// keeps the two charts' colors from ever coinciding and reading as the same
// category.
const PIE_COLOR_OFFSET = 2;

const KIND_LABEL: Record<AccountKind, string> = {
  MUTUAL_FUND: "Mutual Funds",
  ESOP: "ESOP",
  CASH_ISA: "Cash ISA",
  STOCKS_ISA: "Stocks ISA",
};

function investmentTypeBreakdown(s: PortfolioSummary) {
  const byKind = new Map<AccountKind, number>();
  for (const country of s.countries) {
    for (const account of country.accounts) {
      if (account.valueInr <= 0) continue;
      byKind.set(account.kind, (byKind.get(account.kind) ?? 0) + account.valueInr);
    }
  }
  return Array.from(byKind.entries())
    .map(([kind, valueInr]) => ({ kind, label: KIND_LABEL[kind], valueInr }))
    .sort((a, b) => b.valueInr - a.valueInr);
}

export default function DashboardPage() {
  const summary = usePortfolioSummary();
  const history = usePortfolioHistory("1Y");
  const navigate = useNavigate();
  // App is dark-mode only.
  const isDark = true;

  const chartData = useMemo(() => {
    if (history.status !== "success") return [];
    const points = history.data;
    return [
      {
        name: "Net worth",
        color: "#10b981",
        data: points.map((p) => [new Date(p.date).getTime(), p.netWorthInr] as [number, number]),
      },
    ];
  }, [history]);

  const typeBreakdown = useMemo(() => (summary.status === "success" ? investmentTypeBreakdown(summary.data) : []), [summary]);

  const pieOption = useMemo(
    () => ({
      // The Kumo Chart wrapper only honours `tooltip.dangerousHtmlFormatter`
      // (a plain `formatter` is silently dropped), which is why this used to
      // fall back to ECharts' default tooltip — raw unrounded value, no ₹.
      tooltip: {
        trigger: "item" as const,
        dangerousHtmlFormatter: (params: unknown) => {
          const p = params as { name: string; value: number; percent: number };
          return `${p.name}: ${formatInr(p.value)} (${p.percent.toFixed(2)}%)`;
        },
      },
      series: [
        {
          type: "pie" as const,
          radius: ["50%", "80%"],
          label: { show: false },
          itemStyle: { borderColor: "transparent", borderWidth: 2 },
          // Offset past the color slots used by the country-split bar below,
          // so a matching color between the two never implies a matching category.
          data: typeBreakdown.map((t, i) => ({
            name: t.label,
            value: t.valueInr,
            itemStyle: { color: ChartPalette.categorical(i + PIE_COLOR_OFFSET, true) },
          })),
        },
      ],
    }),
    [typeBreakdown],
  );

  if (summary.status === "loading") {
    return (
      <div className="space-y-8">
        <KpiSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  if (summary.status === "error") {
    return (
      <Empty
        title="Couldn't load your portfolio"
        description={summary.error.message}
        contents={
          <Button variant="primary" onClick={summary.reload}>
            Retry
          </Button>
        }
      />
    );
  }

  const s = summary.data;
  const hasHoldings = s.countries.some((c) => c.accounts.some((a) => a.holdings.length > 0));

  return (
    <div className="space-y-8 pb-8">
      <StalePriceBanner warnings={s.warnings} />

      {!hasHoldings ? (
        <Empty
          icon={<ChartLineUpIcon size={48} className="text-kumo-inactive" />}
          title="No investments yet"
          description="Add your first holding's transaction from Investments to start tracking net worth."
          contents={
            <Button variant="primary" onClick={() => navigate("/investments")}>
              Go to Investments
            </Button>
          }
        />
      ) : (
        <>
          <div>
            <Text variant="secondary" size="sm">
              Net worth · as of {formatRelativeAge(s.asOf)}
            </Text>
            <div className="mt-1 text-3xl font-semibold tracking-tight text-kumo-strong sm:text-4xl">
              {formatInr(s.netWorthInr)}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-kumo-hairline p-4">
              <Text variant="secondary" size="sm">
                Investments
              </Text>
              <div className="mt-1 text-2xl font-semibold text-kumo-strong">{formatInr(s.totals.investmentsInr)}</div>
              <div className="mt-2">
                <GainBadge pct={s.totals.unrealisedGainPct} />
              </div>
            </div>
            <div className="rounded-lg border border-kumo-hairline p-4">
              <Text variant="secondary" size="sm">
                Loans outstanding
              </Text>
              <div className="mt-1 text-2xl font-semibold text-kumo-strong">{formatInr(s.totals.loansOutstandingInr)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <Text variant="heading" as="h2">
                Country split
              </Text>
              <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-kumo-fill">
                {s.countries
                  .filter((c) => c.valueInr > 0)
                  .map((c, i) => {
                    const pct = s.totals.investmentsInr > 0 ? (c.valueInr / s.totals.investmentsInr) * 100 : 0;
                    return (
                      <div
                        key={c.country}
                        style={{ width: `${pct}%`, backgroundColor: ChartPalette.categorical(i, true) }}
                      />
                    );
                  })}
              </div>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                {s.countries
                  .filter((c) => c.valueInr > 0)
                  .map((c, i) => (
                    <ChartLegend.SmallItem
                      key={c.country}
                      name={COUNTRY_LABEL[c.country] ?? c.country}
                      color={ChartPalette.categorical(i, true)}
                      value={formatInr(c.valueInr)}
                    />
                  ))}
              </div>
            </div>

            <div>
              <Text variant="heading" as="h2">
                By investment type
              </Text>
              <div className="mt-2 flex flex-col items-center gap-2 sm:flex-row">
                <Chart echarts={echarts} options={pieOption} isDarkMode height={160} className="w-full sm:w-40" />
                <div className="flex flex-col gap-2">
                  {typeBreakdown.map((t, i) => (
                    <ChartLegend.SmallItem
                      key={t.kind}
                      name={t.label}
                      color={ChartPalette.categorical(i + PIE_COLOR_OFFSET, true)}
                      value={formatInr(t.valueInr)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <Text variant="heading" as="h2">
              Net worth over time
            </Text>
            <div className="mt-3">
              {history.status === "loading" && <ChartSkeleton />}
              {history.status === "error" && <Text variant="error">Couldn't load history.</Text>}
              {history.status === "success" && chartData.length > 0 && (
                <TimeseriesChart
                  echarts={echarts}
                  isDarkMode={isDark}
                  data={chartData}
                  height={280}
                  yAxisTickFormat={(v) => formatInrCompact(v)}
                  tooltipValueFormat={(v) => formatInr(v)}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
